import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, X, Copy, EnvelopeSimple, ChatCircleDots, Camera, WarningCircle } from "@phosphor-icons/react";

const CAT_ICON = {
  "Plumbing": "🚰", "HVAC / Heat / AC": "🌡️", "Electrical": "⚡",
  "Appliance": "🔌", "Pest / Rodent": "🐜", "Lock / Key / Door": "🔑",
  "Leak / Water Damage": "💧", "Roof / Ceiling": "🏠", "Other": "🛠️",
};
const URGENCY_STYLE = {
  normal:    { bg: "bg-slate-700",   label: "Normal" },
  same_day:  { bg: "bg-amber-500",   label: "Same-day" },
  emergency: { bg: "bg-red-600",     label: "Emergency" },
};
const COST_LABEL = { "$": "Under $150", "$$": "$150 – $600", "$$$": "Over $600" };

export default function AITriageButton({ jobId, compact = false, triage: initial = null, onTriaged, onRequestPhotos }) {
  const [triage, setTriage] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => { setTriage(initial); }, [initial]);

  const runTriage = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${jobId}/ai-triage`);
      setTriage(r.data);
      setOpen(true);
      onTriaged?.(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Triage failed"));
    } finally { setBusy(false); }
  };

  const u = URGENCY_STYLE[triage?.urgency] || URGENCY_STYLE.normal;

  if (compact) {
    return (
      <>
        {!triage ? (
          <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); runTriage(); }} disabled={busy}
            data-testid={`triage-compact-${jobId}`}
            className="h-7 px-2 border-2 border-fuchsia-600 text-fuchsia-700 font-black uppercase tracking-widest text-[9px] inline-flex items-center gap-1 disabled:opacity-50 bg-white hover:bg-fuchsia-50">
            <Sparkle weight="fill" size={10}/> {busy ? "…" : "Triage"}
          </button>
        ) : (
          <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
            data-testid={`triage-badge-${jobId}`}
            className={`h-7 px-2 ${u.bg} text-white font-black uppercase tracking-widest text-[9px] inline-flex items-center gap-1 hover:brightness-110`}>
            {CAT_ICON[triage.category] || "🛠️"} {triage.category?.split(" / ")[0]} · {u.label}
          </button>
        )}
        {open && <TriageResultModal triage={triage} onClose={() => setOpen(false)} onRerun={runTriage} rerunning={busy}
                                       onRequestPhotos={onRequestPhotos}/>}
      </>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={runTriage} disabled={busy} data-testid="triage-btn"
          className="h-10 px-4 bg-fuchsia-700 hover:bg-fuchsia-800 text-white font-black uppercase tracking-widest text-xs inline-flex items-center gap-1.5 shadow-[3px_3px_0_#0f172a] disabled:opacity-50">
          <Sparkle weight="fill" size={14}/> {busy ? "Analyzing…" : triage ? "Re-run AI Triage" : "🧠 AI Triage"}
        </button>
        {triage && (
          <button onClick={() => setOpen(true)} data-testid="triage-view-result"
            className={`h-10 px-3 ${u.bg} text-white font-black uppercase tracking-widest text-xs inline-flex items-center gap-1`}>
            {CAT_ICON[triage.category] || "🛠️"} {triage.category} · {u.label} · {triage.cost_bracket}
          </button>
        )}
      </div>
      {open && <TriageResultModal triage={triage} onClose={() => setOpen(false)} onRerun={runTriage} rerunning={busy}
                                     onRequestPhotos={onRequestPhotos}/>}
    </>
  );
}


function TriageResultModal({ triage, onClose, onRerun, rerunning, onRequestPhotos }) {
  const u = URGENCY_STYLE[triage.urgency] || URGENCY_STYLE.normal;
  const a = triage.appliance || {};
  return (
    <div className="fixed inset-0 z-[110] bg-slate-900/80 flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={onClose} data-testid="triage-modal">
      <div className="bg-white w-full max-w-lg shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className={`p-4 ${u.bg} text-white flex items-start justify-between gap-2`}>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80 flex items-center gap-1">
              <Sparkle weight="fill" size={11}/> AI Triage · {triage.confidence} confidence
            </div>
            <div className="mt-1 font-black text-lg leading-tight">
              {CAT_ICON[triage.category] || "🛠️"} {triage.category} · {u.label}
            </div>
            {triage.summary && <div className="mt-1 text-sm opacity-90">{triage.summary}</div>}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center hover:bg-white/10"><X size={18}/></button>
        </div>

        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="p-3 bg-slate-50 border-2 border-slate-200">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Cost bracket</div>
              <div className="font-black text-2xl">{triage.cost_bracket}</div>
              <div className="text-[10px] text-slate-500">{COST_LABEL[triage.cost_bracket] || ""}</div>
            </div>
            <div className="p-3 bg-slate-50 border-2 border-slate-200">
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Photos read</div>
              <div className="font-black text-2xl">{triage.photo_count || 0}</div>
              <div className="text-[10px] text-slate-500">{triage.ran_at ? new Date(triage.ran_at).toLocaleString() : ""}</div>
            </div>
          </div>

          {triage.prep_notes && (
            <div className="p-3 bg-slate-900 text-white" data-testid="triage-prep-notes">
              <div className="text-[9px] font-black uppercase tracking-widest text-emerald-400">Tech prep notes</div>
              <pre className="mt-1 whitespace-pre-wrap font-mono text-[12px] leading-relaxed">{triage.prep_notes}</pre>
            </div>
          )}

          {a.detected && (
            <div className="p-3 border-2 border-emerald-500 bg-emerald-50" data-testid="triage-appliance">
              <div className="text-[9px] font-black uppercase tracking-widest text-emerald-700">🔍 Data-plate detected</div>
              <div className="mt-1 grid grid-cols-2 gap-1 text-[12px]">
                {a.type    && <div><span className="text-slate-500">Type:</span> <b className="uppercase">{a.type}</b></div>}
                {a.brand   && <div><span className="text-slate-500">Brand:</span> <b>{a.brand}</b></div>}
                {a.model   && <div><span className="text-slate-500">Model:</span> <b className="font-mono">{a.model}</b></div>}
                {a.serial  && <div><span className="text-slate-500">Serial:</span> <b className="font-mono">{a.serial}</b></div>}
              </div>
            </div>
          )}

          {triage.needs_more_photos && (
            <div className="p-3 border-2 border-amber-500 bg-amber-50" data-testid="triage-needs-photos">
              <div className="text-[10px] font-black uppercase tracking-widest text-amber-800 flex items-center gap-1">
                <WarningCircle weight="fill" size={12}/> More photos would help
              </div>
              <div className="mt-1 text-[12px] text-slate-800">{triage.photo_request_reason}</div>
              {onRequestPhotos && (
                <button onClick={() => { onRequestPhotos(triage.photo_request_reason); onClose(); }}
                        data-testid="triage-request-photos"
                        className="mt-2 h-9 px-3 bg-amber-600 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
                  <Camera size={12}/> Request from tenant
                </button>
              )}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button onClick={onRerun} disabled={rerunning} data-testid="triage-rerun"
              className="h-10 px-3 border-2 border-slate-400 font-black uppercase text-[10px] tracking-widest">
              {rerunning ? "Analyzing…" : "Re-run"}
            </button>
            <button onClick={onClose} className="flex-1 h-10 bg-slate-900 text-white font-black uppercase text-[11px] tracking-widest">
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
